/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import moment from 'moment';

import LeagueSessionCard from './LeagueSessionCard';
import LeaguesFilters from './LeaguesFilters';
import { getLeaguesApiAxiosInstance } from '../../../../../utils/network';
import * as leaguesApi from '../../../../../api/leaguesApi';
import LoadingErrorComponent from './LoadingComponent';
import { LeagueTypeIds } from '../../../../../utils/types';
import { sportTypeIds } from '../../../../../utils/eventConstants';
import { showErrorPopup } from '../../../../../actions/notification-actions';

const { TENNIS, PICKLEBALL } = sportTypeIds;
const {
    ADULT_TEAM_TENNIS,
    JUNIOR_TEAM_TENNIS,
    CLUB_TENNIS, OTHER_TENNIS,
    CLUB_JUNIOR_TEAM_TENNIS,
    ADULT_TEAM_PICKLEBALL,
    JUNIOR_TEAM_PICKLEBALL,
    CLUB_TEAM_PICKLEBALL,
    OTHER_PICKLEBALL,
    CLUB_JUNIOR_TEAM_PICKLEBALL,
} = LeagueTypeIds;
const tennisLeagueIds = [ADULT_TEAM_TENNIS, JUNIOR_TEAM_TENNIS, CLUB_TENNIS, CLUB_JUNIOR_TEAM_TENNIS, OTHER_TENNIS];
const pickleballLeagueIds = [ADULT_TEAM_PICKLEBALL, JUNIOR_TEAM_PICKLEBALL, CLUB_TEAM_PICKLEBALL, CLUB_JUNIOR_TEAM_PICKLEBALL, OTHER_PICKLEBALL];

const PICKLEBALL_MAX_RATING = 10;
const TENNIS_MAX_RATING = 16;
const RATING_MIN = 1;

const defaultFilterValues = {
    ageMin: 7,
    ageMax: 71,
    startDate: moment(),
    gender: '',
    type: '',
    LeagueSessionType: 0,
    ratingMin: RATING_MIN,
    ratingMax: TENNIS_MAX_RATING,
};

const LeaguesTabContent = ({ onContactClick, clubLeagues }) => {
    const loadingRef = useRef(null);
    const dispatch = useDispatch();
    const [filters, setFilters] = useState(defaultFilterValues);
    const [leagues, setLeagues] = useState([]);
    const [selectedLeagueId, setSelectedLeagueId] = useState(null);
    const [sessions, setSessions] = useState([]);
    const [total, setTotal] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [isLoadingMore, setIsLoadingMore] = useState();
    const [sportTypeId, setSportTypeId] = useState();

    const executeScroll = () => loadingRef.current?.scrollIntoView({ block: 'start' });

    const getLeagueSessions = async ({ leagueId, page }) => {
        try {
            const res = await leaguesApi.getLeagueSessions(getLeaguesApiAxiosInstance(), leagueId, sportTypeId, filters, page);

            if (page) {
                setSessions([
                    ...sessions,
                    ...res.data.results,
                ]);
            } else {
                setSessions(res.data.results);
            }
            setTotal(res.data.totalCount);
            setIsLoading(false);
            setIsLoadingMore(false);
        } catch (error) {
            dispatch(showErrorPopup({ message: 'Error loading league sessions', error }));
            setIsLoading(false);
            setIsLoadingMore(false);
        }

        executeScroll();
    };

    const sortLeagues = (a, b) => {
        return a.name.localeCompare(b.name);
    };

    const getSportTypeForLeague = (leagueTypeId) => {
        if (tennisLeagueIds.includes(leagueTypeId)) {
            setSportTypeId(TENNIS);
        } else if (pickleballLeagueIds.includes(leagueTypeId)) {
            setSportTypeId(PICKLEBALL);
        }
    };

    useEffect(() => {
        if (clubLeagues?.length) {
            const displayLeaguesDropdown = clubLeagues.sort(sortLeagues);
            setLeagues(displayLeaguesDropdown);
            setSelectedLeagueId(displayLeaguesDropdown[0]?.id);
            getSportTypeForLeague(displayLeaguesDropdown[0]?.leagueTypeId);
        }
    }, [clubLeagues]);

    useEffect(() => {
        if (selectedLeagueId && sportTypeId) {
            if (sportTypeId === PICKLEBALL) {
                defaultFilterValues.ratingMax = PICKLEBALL_MAX_RATING;
            } else if (sportTypeId === TENNIS) {
                defaultFilterValues.ratingMax = TENNIS_MAX_RATING;
            }

            setSessions([]);
            getLeagueSessions({ leagueId: selectedLeagueId });
            setIsLoading(true);
        }
    }, [selectedLeagueId, filters, sportTypeId]);

    const handleLoadMore = () => {
        const currentPage = sessions.length / 5 || 1;
        getLeagueSessions({ leagueId: selectedLeagueId, page: currentPage + 1 });
        setIsLoadingMore(true);
    };

    const handleLeagueChange = (e) => {
        const selectedLeagueId = parseInt(e.target.value);
        const selectedLeague = leagues.find(({ id }) => selectedLeagueId === id);

        setSelectedLeagueId(selectedLeagueId);
        getSportTypeForLeague(selectedLeague?.leagueTypeId);
    };

    const getIndexForScroll = (length) => ((Math.ceil(length / 5) - 1) * 5) - 1;

    return (
        <>
            {leagues && leagues.length > 0 && (
                <>
                    <div className="row mt16 mb16">
                        <div className="col-8 col-md-4">
                            <label>League Name</label>
                            {leagues.length === 1 ? (
                                <p style={{ color: 'black', paddingTop: '8px' }}>{leagues[0].name}</p>
                            ) : (
                                <select name="league" className={`form-control`} onChange={handleLeagueChange}>
                                    {leagues?.map(league => <option value={league.id} key={league.id} selected={league.id === selectedLeagueId}>{league.name}</option>)}
                                </select>
                            )}
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-12">
                            <LeaguesFilters
                                filters={filters}
                                setFilters={setFilters}
                                defaultFilterValues={defaultFilterValues}
                                sportTypeId={sportTypeId}
                            />
                        </div>
                    </div>
                </>
            )}
            <div className="row">
                <div className="col-12">
                    {isLoading ? (
                        <LoadingErrorComponent isLoading />
                    ) : (
                        sessions && sessions.length ? (
                            sessions.map((league, index) => {
                                if (sessions.length > 5 && index === getIndexForScroll(sessions.length)) {
                                    return (
                                        <div key={league.sessionId} ref={loadingRef}>
                                            <LeagueSessionCard
                                                ref={loadingRef}
                                                league={league}
                                                onContactClick={onContactClick}
                                                sportTypeId={sportTypeId}
                                            />
                                        </div>
                                    );
                                }
                                return (
                                    <LeagueSessionCard
                                        key={league.sessionId}
                                        league={league}
                                        onContactClick={onContactClick}
                                        sportTypeId={sportTypeId}
                                    />
                                );
                            })
                        ) : (
                            <LoadingErrorComponent />
                        )
                    )}
                </div>
            </div>
            {sessions?.length < total && !isLoading && (
                <div className="row mt16">
                    <div className="col-12">
                        <button
                            type="button"
                            className="btn btn-xl btn-default text-uppercase"
                            onClick={handleLoadMore}
                            disabled={isLoadingMore}
                        >
                            {isLoadingMore ? 'Loading...' : 'Load More'}
                        </button>
                    </div>
                </div>
            )}
        </>
    );
};

export default LeaguesTabContent;
